import { type ExtensionFieldValueObjectType } from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import {
  ArenaObjectType,
  ArenaGridRow,
  InvestmentEntityObjectType,
  InvestmentEntityGridRow,
  InvestmentEntityWithExtensionFieldsGridRow,
  EntityExtensionFieldValue,
} from "../types";

/**
 * Transforms a GraphQL arena object to a grid-compatible row format
 */
export function transformArenaToGridRow(arena: ArenaObjectType): ArenaGridRow {
  // Extract database ID from GraphQL global ID
  const dbId = toDbId(arena.id);

  return {
    id: dbId.toString(),
    name: arena.name || "",
    domain: arena.domain || "",
    logoUrl: arena.logoUrl,
  };
}

/**
 * Transforms an array of GraphQL arena objects to grid-compatible rows
 */
export function transformArenasToGridRows(
  arenas: (ArenaObjectType | null | undefined)[],
): ArenaGridRow[] {
  return arenas
    .filter((arena): arena is ArenaObjectType => {
      // Filter out null/undefined arenas and arenas without required fields
      return arena != null && arena.id != null && arena.name != null;
    })
    .map(transformArenaToGridRow);
}

/**
 * Transforms a GraphQL investment entity object to a grid-compatible row format
 */
export function transformInvestmentEntityToGridRow(
  entity: InvestmentEntityObjectType,
): InvestmentEntityGridRow {
  // Extract database ID from GraphQL global ID
  const dbId = toDbId(entity.id);

  return {
    id: dbId.toString(),
    name: entity.name || "",
    arena: entity.arena?.name || "",
    status: entity.status || "",
    isPartial: entity.isPartial || false,
    type: entity.type
      ? {
          id: entity.type.id,
          name: entity.type.name || "",
        }
      : null,
  };
}

/**
 * Transforms extension field values array to a map keyed by field ID
 */
export function transformExtensionFieldValuesToMap(
  extensionFieldValues: (ExtensionFieldValueObjectType | null | undefined)[],
): Record<string, ExtensionFieldValueObjectType> {
  const valueMap: Record<string, ExtensionFieldValueObjectType> = {};

  extensionFieldValues.forEach((value) => {
    if (value?.extensionField?.id) {
      valueMap[value.extensionField.id] = value;
    }
  });

  return valueMap;
}

/**
 * Transforms investment entities with extension field data directly from GraphQL response
 */
export function transformInvestmentEntitiesToGridRowsWithExtensionFields(
  entities: (InvestmentEntityObjectType | null | undefined)[],
): InvestmentEntityWithExtensionFieldsGridRow[] {
  return entities
    .filter((entity): entity is InvestmentEntityObjectType => {
      // Filter out null/undefined entities and entities without required fields
      return entity != null && entity.id != null && entity.name != null;
    })
    .map((entity) => {
      // Transform base entity data using existing function
      const baseEntity = transformInvestmentEntityToGridRow(entity);

      // Extract and transform extension field values from dataVaultCustomFields
      const extensionFieldValues: ExtensionFieldValueObjectType[] = [];
      if (entity.dataVaultCustomFields) {
        entity.dataVaultCustomFields.forEach((tuple) => {
          if (tuple?.values) {
            extensionFieldValues.push(
              ...tuple.values.filter(
                (value): value is ExtensionFieldValueObjectType => value != null,
              ),
            );
          }
        });
      }

      // Transform extension field values to map format
      const extensionFieldValuesMap = transformExtensionFieldValuesToMap(extensionFieldValues);

      return {
        ...baseEntity,
        extensionFieldValues: extensionFieldValuesMap,
      };
    });
}

export function getFieldValuesForSection(
  fields: { id: string }[],
  fieldValuesMap: Map<string, EntityExtensionFieldValue>,
): EntityExtensionFieldValue[] {
  return fields
    .map((field) => fieldValuesMap.get(field.id))
    .filter((fieldValue): fieldValue is EntityExtensionFieldValue => fieldValue !== undefined);
}
